import { useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'app/store';
import {
  getCreateReleaseValidations,
  getRelease,
  getReleaseExportLink,
  getSoundCloudPlaylistTypes,
} from 'app/store/Releases/selectors';
import {
  fetchCreateReleaseValidationsEffect,
  fetchSoundCloudPlaylistTypesDDLEffect,
} from 'app/store/Releases/effects';
import {
  MultiTrackReleaseExport,
  ReleaseHeader,
  ReleaseVersionsList,
  SingleTrackReleaseExport,
} from 'app/components/Releases';
import {
  LabelV2Release,
  ReleaseExportStatus,
  ReleaseType,
  SoundCloudRelease,
  TuneCoreRelease,
} from 'types/releases';
import { ChakraAppLoader } from 'app/components/Core';
import { useHandleProviderConnectionPayload } from 'app/components/Releases/ConnectToProviderDialog/hooks';
import { getReleaseUIState } from 'app/views/Release/utils';
import {
  useCheckConnection,
  useDraftReleaseConnection,
  useFetchAccountConnection,
  useFetchRelease,
  useReleaseExport,
} from 'app/views/Release/hooks';
import { useResource } from 'app/utils/hooks';
import { fetchSongDDLEffect } from 'app/store/SongList/effects';
import { getSongDDLSelector } from 'app/store/SongList/selectors';
import { ExportTuneCoreReleaseDialog } from 'app/components/Releases/ExportTuneCoreReleaseDialog';
import { AccountConnectionProviderId } from 'types/profile';
import { ConnectToProviderDialog } from 'app/components/Releases/ConnectToProviderDialog';
import { useLabelV2FeatureFlag } from 'featureFlags';
import { LabelV2VersionsList } from 'app/components/Releases/LabelV2VersionsList';
import { ExportSendLabelV2Release } from 'app/components/Releases/ExportSendLabelV2Release';
import { QuotaExceededAlert } from './QuotaExceededAlert';

export const Release = () => {
  const dispatch = useAppDispatch();
  const releaseValidation = useAppSelector(getCreateReleaseValidations);
  const { labelV2ReleaseEnabled } = useLabelV2FeatureFlag();
  const release = useAppSelector(getRelease);
  const releaseExportLink = useAppSelector(getReleaseExportLink);
  const playlistTypes = useAppSelector(getSoundCloudPlaylistTypes);
  const [songDDL] = useResource(fetchSongDDLEffect, getSongDDLSelector);
  const [draftExport, setDraftExport] = useState<{
    exportGuid: string;
    providerId: AccountConnectionProviderId;
  }>();
  useHandleProviderConnectionPayload(release);

  const quotaExceeded = useMemo(() => {
    const soundCloudRelease = release as SoundCloudRelease | undefined;
    if (soundCloudRelease?.latestExport) {
      return (
        soundCloudRelease.latestExport.status ===
        ReleaseExportStatus.QUOTA_EXCEEDED
      );
    }
    return false;
  }, [release]);

  const { loading } = useFetchRelease();
  const scConnection = useFetchAccountConnection('SoundCloud');
  const tcConnection = useFetchAccountConnection('TuneCore');
  const {
    handleOnConnect,
    isOpen,
    onOpen,
    onClose,
  } = useDraftReleaseConnection(draftExport?.exportGuid);
  const {
    exportSoundCloudRelease,
    exportTuneCoreRelease,
    exportConnexRelease,
  } = useReleaseExport();
  const { checkConnection } = useCheckConnection();

  useEffect(() => {
    if (!releaseValidation) {
      dispatch(fetchCreateReleaseValidationsEffect());
    }
  }, [dispatch, releaseValidation]);

  const {
    renderSingleTrackBtn,
    renderMultiTrackBtn,
    renderTuneCoreBtn,
    disableSingletrack,
    disableMultitrack,
    disableTuneCoreBtn,
  } = getReleaseUIState({ release, releaseExportLink, playlistTypes });

  const fetchPlaylistTypesDDL = async () => {
    await dispatch(fetchSoundCloudPlaylistTypesDDLEffect());
  };

  const afterReleaseExport = async (
    providerId: AccountConnectionProviderId,
    exportGuid: string,
    connectionGuid?: string
  ) => {
    if (!connectionGuid) {
      setDraftExport({
        exportGuid,
        providerId,
      });
      onOpen();
    }
  };

  useEffect(() => {
    if (playlistTypes.length === 0) {
      fetchPlaylistTypesDDL();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return <ChakraAppLoader />;
  }

  return (
    <>
      {release && (
        <ReleaseHeader release={release}>
          <>
            {renderTuneCoreBtn && (
              <ExportTuneCoreReleaseDialog
                release={release as TuneCoreRelease}
                releaseExportLink={releaseExportLink}
                genresDDL={songDDL?.genres || {}}
                exportRelease={exportTuneCoreRelease}
                isDisabled={disableTuneCoreBtn}
                connection={tcConnection.connection}
                fetchAccountConnection={tcConnection.fetchAccountConnection}
                checkConnectionStatus={checkConnection}
                afterExport={afterReleaseExport}
              />
            )}
            {labelV2ReleaseEnabled && release.type === ReleaseType.LABEL_V2 && (
              <ExportSendLabelV2Release
                exportRelease={exportConnexRelease}
                isReadyForExportReason={
                  (release as LabelV2Release).isReadyForExportReason
                }
                handleOnConnect={handleOnConnect}
                releaseExportLink={{ href: '/' }}
                isInvalid={(release as LabelV2Release).isInvalid ?? true}
              />
            )}
            {renderSingleTrackBtn && (
              <SingleTrackReleaseExport
                releaseTitle={release.title}
                releaseExportLink={releaseExportLink}
                connectionGuid={scConnection.connection?.guid}
                exportRelease={exportSoundCloudRelease}
                checkConnectionStatus={checkConnection}
                afterExport={afterReleaseExport}
                isDisabled={disableSingletrack}
              />
            )}
            {renderMultiTrackBtn && (
              <MultiTrackReleaseExport
                releaseTitle={release.title}
                releaseExportLink={releaseExportLink}
                connectionGuid={scConnection.connection?.guid}
                playlistTypes={playlistTypes}
                genresDDL={songDDL?.genres || {}}
                exportRelease={exportSoundCloudRelease}
                checkConnectionStatus={checkConnection}
                afterExport={afterReleaseExport}
                isDisabled={disableMultitrack}
              />
            )}
            {draftExport && (
              <ConnectToProviderDialog
                releaseGuid={release.guid}
                releaseExportGuid={draftExport.exportGuid}
                providerId={draftExport.providerId}
                isOpen={isOpen}
                onClose={onClose}
                checkConnectionStatus={checkConnection}
                handleConnect={handleOnConnect}
              />
            )}
          </>
        </ReleaseHeader>
      )}
      {quotaExceeded && <QuotaExceededAlert />}
      {release?.type && (
        <>
          {[ReleaseType.LABEL_V2].includes(release.type) && (
            <LabelV2VersionsList />
          )}
          {[ReleaseType.SOUNDCLOUD, ReleaseType.TUNECORE].includes(
            release.type
          ) && <ReleaseVersionsList />}
        </>
      )}
    </>
  );
};
